/**
 * The student's confirmed voice + language, persisted server-side so it follows
 * them across browsers.
 *
 * Keyed on the provider voice, not the SigIQ catalog id: catalog ids are
 * localized personas over a shared provider voice, so the provider voice is
 * what the student actually picked and survives a rename.
 */
import {
  AUDIO_VOICE_MAP,
  AudioVoice,
  DEFAULT_AUDIO_VOICE,
  getBetaVoicesForLanguage,
  getProviderVoiceId,
  getVoiceProvider,
  getVoicesForLanguage,
} from "@/constants/audioVoice";
import ALL_LANGUAGE_OPTIONS, {
  MultilingualOption,
} from "@/constants/multilingual";
import api from "@/utils/axios-utils";
import { useAuthStore } from "@/store/useAuthStore";

export interface PreferredVoice {
  provider: string;
  providerVoiceId: string;
  lang: string;
  speed: string;
  updatedAtMs?: number;
}

/** Shape-check a wire value before it can reach form state. */
export const isPreferredVoice = (value: unknown): value is PreferredVoice => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.provider === "string" &&
    typeof v.providerVoiceId === "string" &&
    typeof v.lang === "string" &&
    typeof v.speed === "string" &&
    Boolean(v.provider && v.providerVoiceId && v.lang && v.speed)
  );
};

/**
 * Searches only the voices offered for that language: scanning AUDIO_VOICE_MAP
 * would also match the legacy alias entries sharing these providerVoiceIds,
 * which no picker can show as selected. Null when the pair no longer resolves.
 */
export const resolvePreferredVoice = (
  pref: PreferredVoice,
  { includeBeta = false }: { includeBeta?: boolean } = {},
): { voice: AudioVoice; language: MultilingualOption } | null => {
  const language = ALL_LANGUAGE_OPTIONS.find((l) => l.value === pref.lang);
  if (!language) return null;

  const offered = [
    ...getVoicesForLanguage(pref.lang),
    ...(includeBeta ? getBetaVoicesForLanguage(pref.lang) : []),
  ];
  const voice = offered.find(
    (v) =>
      getVoiceProvider(v) === pref.provider &&
      getProviderVoiceId(v) === pref.providerVoiceId,
  );
  if (!voice) return null;

  return { voice, language };
};

/**
 * Retry only what's actually transient: no response (network) or 5xx. A 4xx
 * — including 401 — means the request itself is wrong or unauthenticated;
 * nothing in this app refreshes a token in the background, so a retry
 * wouldn't have anything different to send.
 */
const isRetriableError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } } | undefined)
    ?.response?.status;
  return status === undefined || status >= 500;
};

// The server write is a blind SET (no version/timestamp guard) — whichever
// request lands last wins. A retried old choice could otherwise land after a
// newer choice's PUT already succeeded; this lets a stale retry bail instead.
let latestRequestedPref: PreferredVoice | null = null;

/**
 * Fire-and-forget: never blocks a save button or modal transition. If both
 * attempts fail (or a retry is skipped as stale), the write is only logged —
 * the local store stays optimistically updated, but reverts on this same
 * browser's next hydrateUserConfig() (e.g. next reload), since the server
 * never got it.
 */
export const recordPreferredVoice = (
  voiceId: string,
  lang: string,
  speed: string,
): void => {
  const voice = AUDIO_VOICE_MAP[voiceId] ?? DEFAULT_AUDIO_VOICE;
  const pref: PreferredVoice = {
    provider: getVoiceProvider(voice),
    providerVoiceId: getProviderVoiceId(voice),
    lang,
    speed,
  };
  latestRequestedPref = pref;

  // Update the store's copy before the request, not after it. FormProvider
  // seeds from user.config.preferredVoice over the local cache on mount, and
  // nothing refreshes that copy until the next hydrateUserConfig — so leaving
  // it stale means the seed overrides the choice just made, until a reload.
  const { user, setUserConfig } = useAuthStore.getState();
  setUserConfig({ ...(user?.config ?? {}), preferredVoice: pref });
  const requestedByUserId = user?.id;

  void api.put("/preferences/preferred-voice", pref).catch(async (err) => {
    if (!isRetriableError(err)) {
      console.warn("[preferredVoice] Failed to record choice:", err);
      return;
    }
    await new Promise((r) => setTimeout(r, 500));
    if (latestRequestedPref !== pref) {
      // Superseded by a newer choice — that call owns the write now.
      return;
    }
    // Logout is a route change, not a reload, so this timer survives it.
    // The interceptor attaches whatever token is in localStorage at send
    // time — if a different student logged in on this tab while the retry
    // waited, sending it now would attach their identity to this choice.
    if (useAuthStore.getState().user?.id !== requestedByUserId) {
      return;
    }
    try {
      await api.put("/preferences/preferred-voice", pref);
    } catch (retryErr) {
      console.warn(
        "[preferredVoice] Failed to record choice after retry:",
        retryErr,
      );
    }
  });
};
